import React from "react";

const ROW_LABELS = {
  developer: ["Top contributor", "Strong momentum", "Close behind"],
  epic: ["Highest load", "Needs attention", "Close behind"]
};

const BREAKDOWN_MAP = {
  task: { icon: "🎯", label: "Tasks" },
  story: { icon: "📖", label: "Stories" },
  bug: { icon: "🐛", label: "Bugs" },
  other: { icon: "❓", label: "Other" }
};

const ASCENDING_ROW_LABELS = {
  developer: ["Lowest load", "Building up", "Higher load"],
  epic: ["Lower load", "Building up", "Higher load"]
};

const Leaderboard = ({
  data = [],
  title = "Developer Leaderboard",
  subtitle = "Completion count and relative impact",
  variant = "developer",
  sortDirection = "desc",
  entityLabel = "developers",
  nameLabel = "Developer",
  valueLabel = "Bugs", // Passed dynamically from parent now
  emptyMessage = "No data available."
}) => {
  const safeData = Array.isArray(data) ? data : [];
  const sortedData = [...safeData].sort((a, b) =>
    sortDirection === "asc" ? a.count - b.count : b.count - a.count
  );
  const maxCount = sortedData.reduce(
    (highestCount, entry) => Math.max(highestCount, entry.count || 0),
    0
  ) || 1;

  const getRowLabel = (index) => {
    const labelSet = sortDirection === "asc" ? ASCENDING_ROW_LABELS : ROW_LABELS;
    const rowLabels = labelSet[variant] || labelSet.developer;
    return rowLabels[index] || `Active ${nameLabel.toLowerCase()}`;
  };

  const getInitial = (name) => {
    const trimmedName = typeof name === "string" ? name.trim() : "";
    return trimmedName.charAt(0).toUpperCase() || "?";
  };

  const renderBreakdown = (breakdown = {}, total = 0) => {
    return Object.entries(breakdown)
      .filter(([, count]) => count > 0)
      .map(([key, count]) => {
        const breakdown = BREAKDOWN_MAP[key] || BREAKDOWN_MAP.other;
        const share = total ? Math.round((count / total) * 100) : 0;
        return (
          <span key={key} className="breakdown-pill">
            {breakdown.icon} {breakdown.label}: <strong>{count}</strong> <span className="breakdown-share">({share}%)</span>
          </span>
        );
      });
  };

  return (
    <div className={`card leaderboard-card leaderboard-card--${variant}`}>
      <div className="leaderboard-header">
        <div>
          <h2 className="section-title">{title}</h2>
          <p className="section-muted">{subtitle}</p>
        </div>
        <div className={`leaderboard-chip leaderboard-chip--${variant}`}>
          {sortedData.length} {variant === "developer" ? "active" : "total"}
        </div>
      </div>

      <div className="table-shell">
        {sortedData.length === 0 ? (
          <p className="leaderboard-empty">{emptyMessage}</p>
        ) : (
          <table className="modern-table">
            <thead>
              <tr>
                <th>#</th>
                <th>{nameLabel}</th>
                <th>Impact</th>
                <th>{valueLabel}</th>
              </tr>
            </thead>

            <tbody>
              {sortedData.map((entry, index) => (
                <tr
                  key={`${entry.name}-${index}`}
                  className={sortDirection === "desc" && index === 0 ? "top-row" : ""}
                >
                  <td>
                    <span className={`rank rank-${Math.min(index + 1, 3)}`}>
                      {index + 1}
                    </span>
                  </td>

                  <td className="table-cell-entry">
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <div className={`entry-avatar entry-avatar--${variant}`}>
                        {getInitial(entry.name)}
                      </div>
                      <div className="entry-meta">
                        <span className="entry-name">{entry.name}</span>
                        <span className="entry-role">{getRowLabel(index)}</span>
                        
                        {/* THE BREAKDOWN ROW: Only shows for developers with breakdown data */}
                        {variant === "developer" && entry.breakdown && (
                          <div className="breakdown-row">
                            {renderBreakdown(entry.breakdown, entry.count)}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>

                  <td>
                    <div className="progress-bar leaderboard-bar">
                      <div
                        className={`progress-fill progress-fill--${variant}`}
                        style={{ width: `${(entry.count / maxCount) * 100}%` }}
                      />
                    </div>
                  </td>

                  <td>
                    <span className="badge">{entry.count}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Leaderboard;